/**
 * Informant portal (ADHD-21): public page reached from a private link sent to
 * someone who knows an adult client well. No account. Consent first, then the
 * questionnaire. Wording is a draft for clinical review.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import api from '../services/api';

const ENDED = {
  not_found: 'This link is not valid. Please check you copied the whole link from the email.',
  expired: 'This link has expired. If you would still like to help, please ask the person who contacted you to send a new one.',
  revoked: 'This request has been withdrawn, so there is nothing more to do. Thank you.',
  completed: 'You have already sent your answers. Thank you for your help.'
};

const RELATIONSHIP_WORDS = { partner: 'partner', parent: 'parent', sibling: 'brother or sister', friend: 'friend', other: 'someone who knows them well' };

function Shell({ children }) {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f6f8fb', py: { xs: 2, sm: 5 }, px: 2 }}>
      <Paper sx={{ maxWidth: 760, mx: 'auto', p: { xs: 2.5, sm: 4 } }} elevation={0} variant="outlined">
        <Typography variant="overline" color="text.secondary">AIMHI</Typography>
        {children}
      </Paper>
    </Box>
  );
}

export default function InformantPortal() {
  const { token } = useParams();
  const [view, setView] = useState(null);
  const [ended, setEnded] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get(`/informant-portal/${token}`)
      .then(res => {
        if (ENDED[res.data.status]) setEnded(res.data.status);
        else setView(res.data);
      })
      .catch(err => setEnded(err.response?.status === 404 ? 'not_found' : (err.response?.data?.status || 'not_found')));
  }, [token]);

  const name = view?.clientFirstName || 'them';
  const fill = (text) => String(text || '').replace(/\{name\}/g, name);
  const questions = useMemo(() => view?.form?.questions || [], [view]);
  const missingRequired = questions.filter(q => q.required && (answers[q.questionId] === undefined || answers[q.questionId] === ''));

  const agree = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`/informant-portal/${token}/consent`);
      setView(v => ({ ...v, consentGiven: true }));
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  const send = async () => {
    setBusy(true); setError(null);
    try {
      const payload = Object.entries(answers).map(([questionId, answerValue]) => ({ questionId, answerValue }));
      await api.post(`/informant-portal/${token}/submit`, { answers: payload });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Your answers could not be sent. Please try again.');
    } finally { setBusy(false); }
  };

  if (!view && !ended) return <Shell><Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box></Shell>;
  if (ended) return <Shell><Typography variant="h5" sx={{ mb: 2 }}>Thank you</Typography><Typography>{ENDED[ended]}</Typography></Shell>;
  if (sent) {
    return (
      <Shell>
        <Typography variant="h5" sx={{ mb: 2 }}>Thank you, {view.informantFirstName}</Typography>
        <Typography>Your answers have been sent to {name}'s clinician. You can close this page.</Typography>
      </Shell>
    );
  }

  if (!view.consentGiven) {
    return (
      <Shell>
        <Typography variant="h5" sx={{ mb: 2 }}>{name} has asked for your view</Typography>
        <Typography sx={{ mb: 2 }}>
          {name} is taking part in an assessment with their therapist and has agreed that we can ask you, as their {RELATIONSHIP_WORDS[view.relationship] || 'contact'}, some questions about everyday things you have noticed.
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Before you start</Typography>
        <ul style={{ marginTop: 4 }}>
          <li><Typography>It takes about ten minutes. There are no right or wrong answers, and you can say "Don't know".</Typography></li>
          <li><Typography>Your answers go to {name}'s clinician and may be talked about with {name}.</Typography></li>
          <li><Typography>Nothing is sent until you press Send at the end. You can stop at any time.</Typography></li>
          <li><Typography>Taking part is your choice. Your answers are stored securely by AIMHI.</Typography></li>
        </ul>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Button variant="contained" onClick={agree} disabled={busy}>I understand and want to take part</Button>
      </Shell>
    );
  }

  return (
    <Shell>
      <Typography variant="h5" sx={{ mb: 1 }}>Your view of {name}</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>Answer what you can. Choose "Don't know" if you are not sure.</Typography>
      {questions.map((q) => (
        <Box key={q.questionId} sx={{ mb: 2.5 }}>
          {q.instructions && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{fill(q.instructions)}</Typography>
            </>
          )}
          <Typography sx={{ mb: 0.5 }}>{fill(q.text)}{q.required ? ' *' : ''}</Typography>
          {q.answerType === 'text_input_long' || q.answerType === 'text_input_short' ? (
            <TextField fullWidth multiline={q.answerType === 'text_input_long'} minRows={3}
              value={answers[q.questionId] || ''} inputProps={{ maxLength: 4000, 'aria-label': fill(q.text) }}
              onChange={e => setAnswers(a => ({ ...a, [q.questionId]: e.target.value }))} />
          ) : (
            <FormControl>
              <RadioGroup row value={answers[q.questionId] === undefined ? '' : String(answers[q.questionId])}
                onChange={e => {
                  const opt = (q.answerOptions || []).find(o => String(o.value) === e.target.value);
                  setAnswers(a => ({ ...a, [q.questionId]: opt ? opt.value : e.target.value }));
                }}>
                {(q.answerOptions || []).map(o => (
                  <FormControlLabel key={String(o.value)} value={String(o.value)} control={<Radio size="small" />} label={o.label} />
                ))}
              </RadioGroup>
            </FormControl>
          )}
        </Box>
      ))}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {missingRequired.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Please answer the questions marked * before sending.</Typography>
      )}
      <Button variant="contained" onClick={send} disabled={busy || missingRequired.length > 0}>Send my answers</Button>
    </Shell>
  );
}
